import { useCallback, useMemo } from 'react';

import { useGetEmployeeAbsenceData } from '~/data/absences/useGetEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { EnumListKey, useAllEnums } from '~/data/enums';
import {
  filterDatesByUniqueDays,
  getDatesInRange,
  getDaysInRange,
  isDateWithinRange,
  sortDates,
} from '~/utils/helper';

type CalculateAbsenceDaysPropsType = {
  startDate: Date;
  endDate: Date;
  weekWorkDaysCount?: number;
  absenceReasonCode?: string;
  excludeDates?: Date[];
};

type GetUncountedWeekDaysPropsType = {
  absenceReasonCode?: string;
  weekWorkDaysCount?: number;
};

type UseAbsenceDaysCalculationOutput = {
  isLoading: boolean;
  getWeekWorkDaysCountByAbsenceReasonCode: (
    absenceReasonCode: string,
  ) => number;
  getUncountedWeekDays: (props: GetUncountedWeekDaysPropsType) => number[];
  getCalculatedAbsenceDays: (props: CalculateAbsenceDaysPropsType) => number;
};

type PropsType = {
  employeeId: number;
};

export const useAbsenceDaysCalculation = (
  props: PropsType,
): UseAbsenceDaysCalculationOutput => {
  const { employeeId } = props;
  const defaultWeekWorkDaysCount = 5;

  const { applicationConfig } = useApplicationConfig();
  const { companyData } = useCompanyData();
  const { getEnums } = useAllEnums();
  const { data: absenceData, isLoading: isLoadingAbsenceData } =
    useGetEmployeeAbsenceData({
      employeeId,
    });

  const ruleSettings = useMemo(() => {
    const leaveTypes = companyData.config.fComLeaveTypes;
    const vacationBalances = absenceData?.vacationBalance;
    const shortAbsenceTypes = getEnums(EnumListKey.ABSENCE_SHORT_REASON);
    const isVacationBanksEnabled =
      !!applicationConfig?.fComConfig?.absConfig?.fComEnableVacationBank;

    const calcRuleForReason = [
      {
        reasons: leaveTypes.fComVacationLeaveTypes,
        settings: vacationBalances?.fVacationBalanceProjDed,
      },
      ...(isVacationBanksEnabled
        ? [
            {
              reasons: leaveTypes.fComVacationBankLeaveTypes1,
              settings: vacationBalances?.fVacationBalanceBank1ProjDed,
            },
            {
              reasons: leaveTypes.fComVacationBankLeaveTypes2,
              settings: vacationBalances?.fVacationBalanceBank2ProjDed,
            },
            {
              reasons: leaveTypes.fComVacationBankLeaveTypes3,
              settings: vacationBalances?.fVacationBalanceBank3ProjDed,
            },
          ]
        : [
            {
              reasons: leaveTypes.fComVacationLeaveExtraTypes,
              settings: vacationBalances?.fVacationBalanceExtraProjDed,
            },
          ]),
    ].map((lt) => {
      return {
        ...lt,
        calcRule: lt.settings?.fProjectedCalcRule,
      };
    });

    return {
      calcRuleForReason,
      leaveTypes,
      vacationBalances,
      shortAbsenceTypes,
    };
  }, [
    companyData.config.fComLeaveTypes,
    absenceData,
    getEnums,
    applicationConfig,
  ]);

  const getWeekWorkDaysCountByAbsenceReasonCode = useCallback(
    (absenceReason: string) => {
      const {
        calcRuleForReason,
        leaveTypes,
        vacationBalances,
        shortAbsenceTypes,
      } = ruleSettings;

      const isLeaveTypeReason = (leaveTypeReasons: string[]) => {
        return leaveTypeReasons.includes(absenceReason);
      };

      const resolveBankCalcRule = () => {
        return calcRuleForReason.find((lt) => isLeaveTypeReason(lt.reasons))
          ?.calcRule;
      };

      const resolveYearlyBankCalcRule = () => {
        const isYearlyReason = isLeaveTypeReason(
          leaveTypes.fComYearlyVacationBalanceTypes,
        );

        const holidayCode = shortAbsenceTypes.find(
          (e) => e.code === absenceReason,
        )?.extCode;
        const yearlyBalances = vacationBalances?.fYearlyVacationBalances;

        return isYearlyReason && yearlyBalances
          ? yearlyBalances.find((b) => b.fHolidayCode === holidayCode)
              ?.fHolidayCalcRule
          : undefined;
      };

      return (
        resolveBankCalcRule() ??
        resolveYearlyBankCalcRule() ??
        defaultWeekWorkDaysCount
      );
    },
    [ruleSettings, defaultWeekWorkDaysCount],
  );

  const getUncountedWeekDays = useCallback(
    (props: { absenceReasonCode?: string; weekWorkDaysCount?: number }) => {
      const { weekWorkDaysCount, absenceReasonCode } = props;

      const absenceBasedCount = !!absenceReasonCode
        ? getWeekWorkDaysCountByAbsenceReasonCode(absenceReasonCode)
        : undefined;
      const numberBasedCount =
        typeof weekWorkDaysCount !== 'undefined'
          ? weekWorkDaysCount
          : defaultWeekWorkDaysCount;

      const workDays = absenceBasedCount || numberBasedCount;
      return [1, 2, 3, 4, 5, 6, 0].slice(workDays, 7);
    },
    [getWeekWorkDaysCountByAbsenceReasonCode],
  );

  /**
   * useAbsenceDaysCalculation
   *
   * @param props: {
   *   startDate            Range start
   *   endDate              Range end
   *   weekWorkDaysCount    Number of weekdays to be counted, starting from Monday
   *   absenceReason        Absence reason code, for determining number ow weekdays to be counted
   *   excludeDates         Dates to exclude from calculation (e.g. public holidays, etc...)
   * }
   */
  const getCalculatedAbsenceDays = useCallback(
    (props: CalculateAbsenceDaysPropsType) => {
      const {
        startDate,
        endDate,
        weekWorkDaysCount,
        absenceReasonCode,
        excludeDates,
      } = props;

      // Get list of non-work weekday ids from the end of the week,
      // based on number of week work days.
      const uncountedWeekDayIds = getUncountedWeekDays({
        absenceReasonCode,
        weekWorkDaysCount,
      });

      // Non-work weekday dates in range
      const offDaysInRange = getDatesInRange(startDate, endDate).filter((d) =>
        uncountedWeekDayIds.includes(d.getDay()),
      );

      // Specified dates to exclude in range (e.g. public holidays, etc...)
      const excludedDatesInRange = !!excludeDates
        ? excludeDates
            .map((date) => new Date(date))
            .filter((date) => isDateWithinRange(date, startDate, endDate))
        : [];

      // Remove duplicate dates & sort
      const invalidAbsenceDates = sortDates(
        filterDatesByUniqueDays([...offDaysInRange, ...excludedDatesInRange]),
      );

      return (
        getDaysInRange(startDate, endDate, true) - invalidAbsenceDates.length
      );
    },
    [getUncountedWeekDays],
  );

  return {
    isLoading: isLoadingAbsenceData,
    getWeekWorkDaysCountByAbsenceReasonCode,
    getUncountedWeekDays,
    getCalculatedAbsenceDays,
  };
};
